import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import * as Popover from '@radix-ui/react-popover';
import React from 'react';

import { cn } from './cn';
import { Loader } from './Loader';
import { Tooltip } from './Tooltip';

export interface SearchComboboxItem {
  key: string;
  text: string;
  description?: string;
  /** Renders the item muted and not selectable. */
  disabled?: boolean;
  /** Tooltip shown when hovering a disabled item (why it cannot be selected). */
  disabledHint?: string;
  [key: string]: any;
}

export interface SearchComboboxProps {
  placeholder?: string;
  value?: string;
  onSearchChange?: (text: string) => void;
  items?: SearchComboboxItem[];
  onItemClick?: (item: SearchComboboxItem) => void;
  onDismiss?: () => void;
  loading?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
  className?: string;
  disabled?: boolean;
  /** Leading icon inside the input (positioned automatically); defaults to a magnifier, pass null to render none. */
  icon?: React.ReactNode | null;
  /** Render custom item content */
  renderItem?: (item: SearchComboboxItem) => React.ReactNode;
  /** Actions to show at the bottom of results */
  actions?: React.ReactNode;
  /** Minimum callout width */
  minWidth?: number;
}

export const SearchCombobox = ({
  placeholder = 'Search...',
  value,
  onSearchChange,
  items,
  onItemClick,
  onDismiss,
  loading,
  emptyMessage = 'No items found.',
  loadingMessage = 'Searching...',
  className,
  disabled,
  icon,
  renderItem,
  actions,
  minWidth,
}: SearchComboboxProps) => {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const searchValue = value !== undefined ? value : internalValue;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  React.useEffect(() => {
    setHighlightedIndex(-1);
  }, [items]);

  const handleSearchChange = (text: string) => {
    if (value === undefined) setInternalValue(text);
    onSearchChange?.(text);
    if (!open) setOpen(true);
  };

  const handleItemClick = (item: SearchComboboxItem) => {
    if (item.disabled) return;
    onItemClick?.(item);
    setOpen(false);
    if (value === undefined) setInternalValue('');
    setHighlightedIndex(-1);
    onDismiss?.();
  };

  /** Moves the highlight to the next enabled item in `direction`, wrapping around; disabled items are skipped. */
  const moveHighlight = (direction: 1 | -1) => {
    setHighlightedIndex((prev) => {
      if (!items?.length) return prev;
      let next = prev;
      for (let step = 0; step < items.length; step++) {
        next += direction;
        if (next >= items.length) next = 0;
        if (next < 0) next = items.length - 1;
        if (!items[next].disabled) {
          itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
          return next;
        }
      }
      return prev;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || !items || items.length === 0) return;

    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      moveHighlight(1);
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      moveHighlight(-1);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleItemClick(items[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const hasResults = items && items.length > 0;
  const showContent = hasResults || loading || (items && items.length === 0);

  return (
    <Popover.Root
      open={open && showContent}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) onDismiss?.();
      }}
    >
      <Popover.Trigger asChild>
        <div className={cn('relative', className)}>
          {icon !== null && (
            <span className="absolute left-3 top-1/2 flex -translate-y-1/2 text-gray-400 pointer-events-none">
              {icon ?? <MagnifyingGlassIcon className="h-4 w-4" />}
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'w-full rounded-md border border-gray-300 bg-surface py-2 pr-3 text-sm text-text-primary placeholder:text-gray-400 transition-colors duration-200',
              icon === null ? 'pl-3' : 'pl-9',
              'focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500',
              disabled && 'bg-gray-50 cursor-not-allowed'
            )}
          />
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 rounded-lg border border-border bg-surface shadow-lg"
          style={{ minWidth: minWidth || 'var(--radix-popover-trigger-width)' }}
          sideOffset={4}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="max-h-72 overflow-y-auto overscroll-contain py-1">
            {loading ? (
              <Loader label={loadingMessage} className="py-4" />
            ) : hasResults ? (
              <>
                {items.map((item, index) => {
                  // Not natively disabled so hover still reaches the tooltip trigger.
                  const itemButton = (
                    <button
                      key={item.key}
                      type="button"
                      ref={(el) => {
                        itemRefs.current[index] = el;
                      }}
                      aria-disabled={item.disabled}
                      onClick={() => handleItemClick(item)}
                      className={cn(
                        'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors duration-150 bg-transparent border-none',
                        item.disabled
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-50 cursor-pointer',
                        highlightedIndex === index && 'bg-gray-100'
                      )}
                    >
                      {renderItem ? (
                        renderItem(item)
                      ) : (
                        <>
                          <span className="font-medium">{item.text}</span>
                          {item.description && <span className="text-xs text-gray-400">{item.description}</span>}
                        </>
                      )}
                    </button>
                  );
                  return item.disabled && item.disabledHint ? (
                    <Tooltip key={item.key} content={item.disabledHint} triggerClassName="w-full">
                      {itemButton}
                    </Tooltip>
                  ) : (
                    itemButton
                  );
                })}
                {actions && <div className="border-t border-border/50 px-3 py-2">{actions}</div>}
              </>
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-400">{emptyMessage}</div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
